import { createApi } from "@reduxjs/toolkit/query/react";
import { apiRequestBaseQuery } from "../../services/rtkBaseQuery";
import { getUserDetails } from "../../services/apiRequest";
import { requestType } from "../../services/requests";

// =============================================================================
// ROYAL MOMENTUM - Brands API
// =============================================================================

export const royalMomentumBrandsApi = createApi({
    reducerPath: "royalMomentumBrandsApi",
    baseQuery: apiRequestBaseQuery,
    tagTypes: ["Brands", "FavoriteBrands", "AccountNodes"],
    endpoints: (builder) => ({
        // Get agency HQ accounts by country (V2).
        // Normalises the response so each agency exposes `uid` (= agency_id_no_r) and `logo`
        // (= logo_url). `agency_id_no_r` is the value every downstream Royal Momentum call sends as
        // `travel_agency_uid`/`travelAgencyUid` (read from `selectedBrand.uid`), so mapping it onto
        // `uid` here keeps all those consumers working without change.
        getTravelAgentCompanies: builder.query({
            query: (countryCode) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getAgencyHqV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        country_code: countryCode,
                    },
                };
            },
            transformResponse: (response) => {
                const agencies = response?.data?.agencies || [];
                const companies = agencies.map((agency) => ({
                    ...agency,
                    uid: agency.agency_id_no_r,
                    logo: agency.logo_url,
                }));
                return { ...response, data: { ...response.data, companies } };
            },
            providesTags: ["Brands"],
        }),

        // Account groups for one market (GET_ACCOUNT_NODES_V2, SPIKE). Without `parentRef` the
        // backend returns the top-level nodes; with one, only that node's direct children. Each
        // node: { account_ref, node_type, name, parent_ref, member_count, child_count, children[] }.
        // `account_ref` is a tree address, NOT an agency key: only AFF:/AGY: nodes map to one
        // (the part after the colon is an agency_id_no_r), which is why selection goes through
        // `brandFromAccountNode` in AccountGroupBrowser rather than passing a node on as a brand.
        //
        // `nodeType` without `parentRef` returns every node of that type at any depth (not just the
        // top level) — the flat per-type list the brand selector's group chips and search run on.
        getAccountNodes: builder.query({
            query: ({ countryCode, parentRef, nodeType }) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getAccountNodesV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        country_code: countryCode,
                        ...(parentRef ? { parent_ref: parentRef } : {}),
                        ...(nodeType ? { node_type: nodeType } : {}),
                    },
                };
            },
            transformResponse: (response) => response?.data?.nodes || [],
            providesTags: ["AccountNodes"],
        }),

        // Get favourite travel agents for the logged-in user (V2).
        // Normalised like the HQ list so each item exposes `uid` (= agency_id_no_r) and `logo`
        // (= logo_url); this keeps `favUids.has(brand.uid)` matching against the HQ list.
        getFavoriteTravelAgents: builder.query({
            // `countryCode` is the market picked at the country selector — the same value
            // `getTravelAgentCompanies` sends, so the favourites come back scoped to the list
            // they are matched against rather than to every market the user has ever favourited.
            query: (countryCode) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.getFavoriteTravelAgentsV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        country_code: countryCode,
                    },
                };
            },
            transformResponse: (response) => {
                const list = response?.data?.agencies || response?.data?.companies || [];
                // Favourites return the canonical key as `agency_id` (already the no-R form),
                // whereas the HQ list carries it as `agency_id_no_r`. Prefer whichever exists so
                // `uid` always matches the HQ list's `uid` for favourite highlighting.
                const companies = list.map((agency) => ({
                    ...agency,
                    uid: agency.agency_id_no_r ?? agency.agency_id ?? agency.uid,
                    logo: agency.logo_url ?? agency.logo,
                }));
                return { ...response, data: { ...response.data, companies } };
            },
            providesTags: ["FavoriteBrands"],
        }),

        // Add a travel agent company to favourites (V2).
        // `agencyId` is the value from `brand.uid` (= agency_id_no_r), sent as `agency_id`.
        // Takes an object rather than the bare id because the write also carries `countryCode`
        // (the selected market) — the remove mutation below still takes the id on its own.
        addFavoriteTravelAgent: builder.mutation({
            query: ({ agencyId, countryCode }) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.addFavoriteTravelAgentV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: agencyId,
                        country_code: countryCode,
                    },
                };
            },
            invalidatesTags: ["FavoriteBrands"],
        }),

        // Remove a travel agent company from favourites (V2).
        // `agencyId` is the value from `brand.uid` (= agency_id_no_r), sent as `agency_id`.
        removeFavoriteTravelAgent: builder.mutation({
            query: (agencyId) => {
                const userDetails = getUserDetails();
                return {
                    requestType: requestType.removeFavoriteTravelAgentV2,
                    data: {
                        user_uid: userDetails?.user_details?.uid,
                        agency_id: agencyId,
                    },
                };
            },
            invalidatesTags: ["FavoriteBrands"],
        }),
    }),
});

// Export hooks for usage in components
export const {
    useGetTravelAgentCompaniesQuery,
    useGetAccountNodesQuery,
    useGetFavoriteTravelAgentsQuery,
    useAddFavoriteTravelAgentMutation,
    useRemoveFavoriteTravelAgentMutation,
} = royalMomentumBrandsApi;
